import type { ComputedRef, WritableComputedRef } from 'vue'
import type { GiftProgressEntry, PendingGift } from '~/utils/giftCopy'

/**
 * `claim` — a rule has gift units to choose; the dialog shows its pool.
 * `added` — a rule's only gift (one fixed variant) was put in the bag by the
 * backend; the dialog just says so.
 */
export type GiftUnlockDialogMode = 'claim' | 'added'

interface GiftUnlockDialogState {
  isOpen: boolean
  mode: GiftUnlockDialogMode
  ruleId: string | null
  /** Gifts dealt with since the card opened: chosen, acknowledged or put off. */
  handled: number
  /** Rules the queue has moved past since the card opened ("Choose later", "Next"). */
  passed: string[]
  /** Whether auto-added gifts join the queue: in the cart drawer, not at checkout. */
  withAdded: boolean
}

/** "1/2": the gift on screen and how many are queued in this opening. */
export interface GiftQueuePosition {
  current: number
  total: number
}

const SEEN_KEY_PREFIX = 'gift-unlock-seen'

/** Gives up waiting for an in-flight add to cart after this long. */
const READY_TIMEOUT_MS = 15_000

/**
 * A rule the backend fulfils on its own: exactly one gift option, of kind
 * VARIANT (contract D1). Only those are celebrated in `added` mode — a gift
 * the customer chose was already acknowledged in the dialog they chose it in.
 */
function isAutoAddedRule(entry: GiftProgressEntry): boolean {
  return entry.rewardMode !== 'CHEAPEST_IN_COLLECTION'
    && entry.giftOptions.length === 1 && entry.giftOptions[0]?.kind === 'VARIANT'
}

/** A different cheapest product is a new offer; size and cart refreshes keep the same dismissal. */
export function giftPendingEventKey(
  gift: Pick<PendingGift, 'ruleId' | 'entitled'> & { options: readonly { product: { id: string | number } }[] },
  rewardMode?: GiftProgressEntry['rewardMode'],
): string {
  const key = `${gift.ruleId}:${gift.entitled}`
  return rewardMode === 'CHEAPEST_IN_COLLECTION'
    ? `${key}:product:${gift.options.map(option => String(option.product.id)).sort().join(',')}`
    : key
}

/**
 * The gift unlock dialog's shared state, and when it opens by itself.
 *
 * One dialog for the whole app (`useState`): the cart drawer asks it to open
 * when the drawer opens (`notifyCartOpened`), and the pending row in the
 * drawer or the checkout summary opens it for its rule (`open(ruleId)`).
 *
 * It opens by itself once per unseen event:
 * - a rule in `Order.pendingGifts` — `claim` mode;
 * - an auto-added gift: a reached `giftProgress` entry of a single-VARIANT
 *   rule with granted units, nothing pending and no pending claim — `added`
 *   mode.
 * "Seen" is remembered for this tab session under order code + rule id +
 * entitled count (and the offered product for a cheapest-item rule), so a
 * changed cheapest product or a repeating rule that earns a further unit counts as a
 * new event, while closing the dialog ("Choose later") keeps it from coming
 * back on every cart open — the pending row stays to reopen it. Every
 * `sessionStorage` touch is wrapped (Safari private mode and hardened
 * profiles throw on it) and backed by an in-memory copy, so the dialog still
 * opens only once per page life when storage is unavailable. Nothing here
 * runs on the server.
 *
 * `notifyCartOpened()` is idempotent: an event that has been shown is never
 * shown again, so it is also safe to call after an in-drawer change. When an
 * add to cart is still in flight (the drawer opens optimistically, with
 * `drawers.cart.adding` set) or the order has not loaded yet, it waits for
 * that to settle and decides then — the gift the add just unlocked is only in
 * the order that add returns — provided the drawer is still open.
 *
 * Several gifts at once are a queue, one card after another with a "1/2"
 * counter (`position`): every unit of every pending claim, then — in the
 * drawer — every auto-added gift not yet shown. A pick counts one step
 * (`completeStep`) and the card moves on (`moveOn`); "Choose later" on a claim
 * and "Next" on an added card pass the rule with all its units (`pass`). The
 * counter is worked out live from what is still waiting plus what has been
 * handled, so a gift that sells out or unlocks while the card is open changes
 * the total instead of leaving it wrong. Closing the card ends the queue; the
 * gifts it did not reach are still unseen and open on the next cart open.
 */
export function useGiftUnlockDialog(): {
  isOpen: WritableComputedRef<boolean>
  mode: WritableComputedRef<GiftUnlockDialogMode>
  ruleId: ComputedRef<string | null>
  position: ComputedRef<GiftQueuePosition | null>
  open: (ruleId?: string | number) => boolean
  close: () => void
  completeStep: () => void
  moveOn: () => boolean
  pass: () => void
  notifyCartOpened: () => void
  notifyCheckoutReady: () => void
  markSeen: (ruleId: string | number) => void
} {
  const state = useState<GiftUnlockDialogState>('gift-unlock-dialog', () => ({
    isOpen: false,
    mode: 'claim',
    ruleId: null,
    handled: 0,
    passed: [],
    withAdded: false,
  }))
  const seenInMemory = useState<string[]>('gift-unlock-seen', () => [])
  const waiting = useState<boolean>('gift-unlock-waiting', () => false)
  const drawers = useDrawers()
  const { order, entries, pendingGifts } = useGiftProgress()

  const isOpen = computed<boolean>({
    get: () => state.value.isOpen,
    set: (value) => {
      state.value.isOpen = value
    },
  })
  const mode = computed<GiftUnlockDialogMode>({
    get: () => state.value.mode,
    set: (value) => {
      state.value.mode = value
    },
  })
  const ruleId = computed(() => state.value.ruleId)

  const pendingRuleIds = computed(() => pendingGifts.value.map(gift => String(gift.ruleId)))

  /** Auto-added gifts on the order, in the backend's order. */
  const addedEntries = computed(() => entries.value.filter(entry =>
    entry.unlocked
    && entry.granted > 0
    && entry.pending === 0
    && !pendingRuleIds.value.includes(String(entry.ruleId))
    && isAutoAddedRule(entry)))

  function entitledFor(id: string): number {
    const claim = pendingGifts.value.find(gift => String(gift.ruleId) === id)
    if (claim)
      return claim.entitled
    return (order.value?.giftProgress ?? []).find(entry => String(entry.ruleId) === id)?.entitled ?? 0
  }

  function seenKey(id: string): string {
    const claim = pendingGifts.value.find(gift => String(gift.ruleId) === id)
    const entry = order.value?.giftProgress.find(candidate => String(candidate.ruleId) === id)
    const event = claim ? giftPendingEventKey(claim, entry?.rewardMode) : `${id}:${entitledFor(id)}`
    return `${SEEN_KEY_PREFIX}:${order.value?.code ?? '-'}:${event}`
  }

  function isSeen(key: string): boolean {
    if (seenInMemory.value.includes(key))
      return true
    if (!import.meta.client)
      return false
    try {
      return window.sessionStorage.getItem(key) === '1'
    }
    catch {
      return false
    }
  }

  function markSeen(id: string | number): void {
    const key = seenKey(String(id))
    if (!seenInMemory.value.includes(key))
      seenInMemory.value = [...seenInMemory.value, key]
    if (!import.meta.client)
      return
    try {
      window.sessionStorage.setItem(key, '1')
    }
    catch {
      // Storage unavailable — the in-memory copy above still covers this page life.
    }
  }

  function modeFor(id: string): GiftUnlockDialogMode {
    return pendingRuleIds.value.includes(id) ? 'claim' : 'added'
  }

  /**
   * Opens the dialog for a rule — or, without one, for the first pending
   * claim, else the first auto-added gift. Marks the rule seen. Returns false
   * when there is nothing to show. Opening a closed card starts a new queue;
   * opening a rule while the card is open moves the queue on to it.
   */
  function open(id?: string | number): boolean {
    const target = id != null && id !== ''
      ? String(id)
      : (pendingRuleIds.value[0] ?? (addedEntries.value[0] ? String(addedEntries.value[0].ruleId) : undefined))
    if (!target)
      return false
    // Nothing to show for a rule that is neither pending nor an auto-added
    // gift on this order copy — opening would draw an empty "in your basket"
    // dialog.
    if (!pendingRuleIds.value.includes(target) && !addedEntries.value.some(entry => String(entry.ruleId) === target))
      return false
    const fresh = !state.value.isOpen
    state.value = {
      isOpen: true,
      mode: modeFor(target),
      ruleId: target,
      handled: fresh ? 0 : state.value.handled,
      passed: fresh ? [] : state.value.passed,
      withAdded: fresh ? drawers.value.cart.open : state.value.withAdded,
    }
    markSeen(target)
    return true
  }

  /** Closes the dialog. The rule and mode are kept so the closing animation does not redraw. */
  function close(): void {
    state.value.isOpen = false
  }

  // ---- Queue ----------------------------------------------------------------

  /**
   * The gifts still ahead in this opening, the one on screen included: every
   * unit of every pending claim not passed, then (in the drawer) every
   * auto-added gift not yet shown. An added card on screen was marked seen as
   * it opened, so it counts itself.
   */
  const queueLeft = computed(() => {
    const { isOpen, mode, ruleId: current, passed, withAdded } = state.value
    if (!isOpen)
      return 0
    const claims = pendingGifts.value
      .filter(gift => gift.remaining > 0 && !passed.includes(String(gift.ruleId)))
      .reduce((sum, gift) => sum + gift.remaining, 0)
    const added = addedEntries.value.filter((entry) => {
      const id = String(entry.ruleId)
      if (passed.includes(id))
        return false
      if (mode === 'added' && id === current)
        return true
      return withAdded && !isSeen(seenKey(id))
    }).length
    return claims + added
  })

  /** Null unless more than one gift is queued. */
  const position = computed<GiftQueuePosition | null>(() => {
    const total = state.value.handled + queueLeft.value
    if (!state.value.isOpen || total < 2)
      return null
    return { current: Math.min(state.value.handled + 1, total), total }
  })

  /** The rule after the one on screen: a pending claim, else (in the drawer) an auto-added gift not yet shown. */
  function nextInQueue(): string | undefined {
    const { ruleId: current, passed, withAdded } = state.value
    const claim = pendingGifts.value.find((gift) => {
      const id = String(gift.ruleId)
      return gift.remaining > 0 && id !== current && !passed.includes(id)
    })
    if (claim)
      return String(claim.ruleId)
    if (!withAdded)
      return undefined
    return addedEntries.value
      .map(entry => String(entry.ruleId))
      .find(id => id !== current && !passed.includes(id) && !isSeen(seenKey(id)))
  }

  /** Counts the gift on screen as handled: one unit was just chosen. */
  function completeStep(): void {
    state.value.handled += 1
  }

  /** Opens the next gift in the queue. False when none is left; the card is then left as it is. */
  function moveOn(): boolean {
    const next = nextInQueue()
    return next ? open(next) : false
  }

  /**
   * "Choose later" on a claim, "Next" on an added card: the rule on screen
   * leaves the queue with every unit it still has, and the card moves on to
   * the next gift or closes. A passed claim stays in the cart's pending row.
   */
  function pass(): void {
    const id = state.value.ruleId
    if (id && !state.value.passed.includes(id)) {
      const claim = state.value.mode === 'claim'
        ? pendingGifts.value.find(gift => String(gift.ruleId) === id)
        : undefined
      state.value.handled += Math.max(1, claim?.remaining ?? 1)
      state.value.passed = [...state.value.passed, id]
    }
    if (!moveOn())
      close()
  }

  function firstUnseen(): string | undefined {
    const claim = pendingRuleIds.value.find(id => !isSeen(seenKey(id)))
    if (claim)
      return claim
    return addedEntries.value
      .map(entry => String(entry.ruleId))
      .find(id => !isSeen(seenKey(id)))
  }

  function openFirstUnseen(): void {
    if (state.value.isOpen || !drawers.value.cart.open)
      return
    const target = firstUnseen()
    if (target)
      open(target)
  }

  const ready = computed(() => !drawers.value.cart.adding && order.value !== undefined)

  function notifyCartOpened(): void {
    if (!import.meta.client || state.value.isOpen)
      return
    if (ready.value) {
      openFirstUnseen()
      return
    }
    if (waiting.value)
      return
    waiting.value = true

    // Created outside any component setup, so it is stopped by hand — on the
    // first ready state or after the timeout, whichever comes first.
    let timer: ReturnType<typeof setTimeout> | undefined
    const stop = watch(ready, (isReady) => {
      if (!isReady)
        return
      finish()
      openFirstUnseen()
    })
    function finish() {
      stop()
      if (timer)
        clearTimeout(timer)
      waiting.value = false
    }
    timer = setTimeout(finish, READY_TIMEOUT_MS)
  }

  /**
   * Checkout's counterpart of `notifyCartOpened`: checkout has no cart drawer,
   * so this opens the first unseen pending CLAIM without waiting for one.
   * Auto-added gifts are not celebrated here — the summary already lists them
   * as lines. Same "seen" memory, so a claim shown or closed with "Choose
   * later" does not come back.
   */
  function notifyCheckoutReady(): void {
    if (!import.meta.client || state.value.isOpen)
      return
    const target = pendingRuleIds.value.find(id => !isSeen(seenKey(id)))
    if (target && open(target))
      state.value.withAdded = false
  }

  return { isOpen, mode, ruleId, position, open, close, completeStep, moveOn, pass, notifyCartOpened, notifyCheckoutReady, markSeen }
}
